import { useSortable } from "@dnd-kit/sortable";
import { faMinus } from "@fortawesome/free-solid-svg-icons/faMinus";
import { faPen } from "@fortawesome/free-solid-svg-icons/faPen";
import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../../components/Button";
import { Spinner } from "../../../../components/Spinner";
import { Lengths } from "../../../../model/shared/enum/Lengths.enum";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { TestIDs } from "../../../../tests/types/testIds";
import { ADD_COLUMN_KEY } from "./constants";
import { DragIndexContext } from "./EntriesTableWithDNDContext";
import { DragIndexState } from "./types/DragIndexState";

const dragActiveStyle = (dragState: DragIndexState, id: string) => {
  const { active, over, direction } = dragState;
  // drag active style
  let style: React.CSSProperties = {};
  if (active && active === id) {
    style = { backgroundColor: "gray", opacity: 0.5 };
  }
  // dragover dashed style
  else if (over && id === over && active !== over) {
    style =
      direction === "right"
        ? { borderRight: "1px dashed gray" }
        : { borderLeft: "1px dashed gray" };
  }
  return style;
};

// The controls are laid over the end of the header rather than beside its
// content. The body's columns don't make room for them, so taking up width
// here would push every header after this one out of line with its rows.
const Th = styled.th`
  .column-header {
    display: flex;
    align-items: center;
    gap: ${Spacing.xs};
  }

  .column-header-content {
    flex: 1;
    min-width: 0;
  }

  .column-controls {
    position: absolute;
    inset-block: 0;
    inset-inline-end: 0;
    display: flex;
    align-items: center;
    padding-inline: ${Spacing.xs};
    background-color: ${({ theme }) => theme.darkMain};
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s;
  }

  /* Leaves the sort arrows, which sit at the very end, visible */
  &.ant-table-column-has-sorters .column-controls {
    inset-inline-end: 1.5rem;
  }

  .column-saving {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }

  &:hover .column-controls,
  .column-controls:focus-within,
  .column-controls.is-busy {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none) {
    .column-controls {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const ControlButton = styled(Button)`
  margin: 0;
  padding: ${Spacing.xs};
`;

const RenameInput = styled.input`
  width: 100%;
  min-width: 0;
  height: 1.75rem;
  padding: 0 ${Spacing.xs};
  border: 1px solid ${({ theme }) => theme.lightgray};
  border-radius: ${Rounded.sm};
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.textColor};
  font: inherit;
  outline: none;
`;

const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

interface HeaderCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  id: string;
  field?: string;
  /** The column's current name. */
  label?: string;
  /** Set on columns that can be hidden - shows the "-" that calls it. */
  onHide?: () => Promise<boolean>;
  /** Set on columns the user can rename - shows the pen that edits the name in place. */
  onRename?: (label: string) => Promise<boolean>;
}

export const EntriesTableWithDNDHeaderCell: React.FC<HeaderCellProps> = ({
  onHide,
  onRename,
  field,
  label,
  ...props
}) => {
  const [hiding, setHiding] = useState(false);
  const [renaming, setRenaming] = useState(false);
  // The name being typed - null while the column isn't being renamed
  const [draft, setDraft] = useState<string | null>(null);
  const discardOnBlur = useRef(false);
  const renameInput = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const hide = async () => {
    setHiding(true);

    // Once hidden this whole cell unmounts, so the spinner is only taken
    // down when nothing was hidden.
    if (!(await onHide?.())) {
      setHiding(false);
    }
  };

  const submitRename = async () => {
    if (draft === null || renaming) {
      return;
    }

    const newLabel = draft.trim();

    if (!newLabel || newLabel === label) {
      setDraft(null);
      return;
    }

    setRenaming(true);
    const saved = await onRename?.(newLabel);
    setRenaming(false);

    if (saved) {
      setDraft(null);
    } else {
      // Left open so the name can be fixed, the error says what was wrong
      renameInput.current?.focus();
    }
  };

  const onRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();

    if (e.key === "Enter") {
      // Saving is the blur's job, so the two ways out can't both save
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      discardOnBlur.current = true;
      e.currentTarget.blur();
      setDraft(null);
    }
  };

  const onRenameBlur = () => {
    if (discardOnBlur.current) {
      discardOnBlur.current = false;
      return;
    }

    submitRename();
  };

  const isDisabled =
    props.id === "0" ||
    props.id === "1" ||
    props.id === "2" ||
    props.id === ADD_COLUMN_KEY;

  const dragState = useContext(DragIndexContext);
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: props.id,
    disabled: {
      draggable: isDisabled,
      droppable: isDisabled,
    },
  });

  const hasControls = Boolean(onHide || onRename);
  const editing = draft !== null;

  const content = editing ? (
    <RenameInput
      ref={renameInput}
      value={draft}
      maxLength={Lengths.NAME}
      dir="auto"
      autoFocus
      disabled={renaming}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => e.currentTarget.select()}
      onBlur={onRenameBlur}
      onKeyDown={onRenameKeyDown}
      onMouseDown={stopPropagation}
      onTouchStart={stopPropagation}
      onClick={stopPropagation}
      data-testid={TestIDs.entries.table.renameColumnInput(field ?? props.id)}
    />
  ) : (
    props.children
  );

  const style: React.CSSProperties = {
    ...props.style,
    cursor: isDisabled ? "default" : "move",
    ...(isDragging
      ? { position: "relative", zIndex: 4, userSelect: "none" }
      : {}),
    ...dragActiveStyle(dragState, props.id),
  };
  return (
    <Th
      {...props}
      ref={setNodeRef}
      style={style}
      // dnd-kit marks a cell that can't be dragged as a disabled button, which
      // would make the controls inside it (the "-", the "+") disabled too
      {...(isDisabled ? {} : attributes)}
      {...listeners}
    >
      {hasControls ? (
        <div className="column-header">
          <div className="column-header-content">{content}</div>

          {editing ? (
            // While the name is being typed the input has the cell to itself
            renaming ? (
              <span className="column-saving">
                <Spinner color="darkerSub" size="100%" />
              </span>
            ) : null
          ) : (
            // Kept out of the header's own drag and sort handling
            <span
              className={`column-controls${hiding ? " is-busy" : ""}`}
              onMouseDown={stopPropagation}
              onTouchStart={stopPropagation}
              onKeyDown={stopPropagation}
              onClick={stopPropagation}
            >
              {onRename ? (
                <ControlButton
                  icon={faPen}
                  backgroundColor="transparent"
                  color="darkerSub"
                  info={t("common.rename")}
                  onClick={() => setDraft(label ?? "")}
                  dataTestId={TestIDs.entries.table.renameColumn(
                    field ?? props.id,
                  )}
                />
              ) : null}

              {onHide ? (
                <ControlButton
                  icon={faMinus}
                  backgroundColor="transparent"
                  color="darkerSub"
                  info={t("common.hide")}
                  loading={hiding}
                  loadingColor="darkerSub"
                  onClick={hide}
                  dataTestId={TestIDs.entries.table.hideColumn(
                    field ?? props.id,
                  )}
                />
              ) : null}
            </span>
          )}
        </div>
      ) : (
        content
      )}
    </Th>
  );
};
